import React, { FC, PropsWithChildren } from 'react';
import { Modal as AntModal } from 'antd';
import { ModalProps } from 'antd/lib/modal/Modal';
import styled from '@emotion/styled';
import { MobXProviderContext, observer } from 'mobx-react';
import useTheme from '@app/hooks/useTheme';
import useMountEffect from '@app/hooks/useMountEffect';
import { GuestLoginBrandedTheme, GuestLoginI18nNamespaceProvider } from '@app/guestLogin/domain/guestLogin';
import GuestLoginEmailFlow from '@app/guestLogin/routes/email/GuestLoginEmailFlow';
import SupplierRegistrationMode from '@supplierRegistration/supplierRegistrationMode';
import {
  guestSession,
  SUPPLIER_REGISTRATION_GUEST_LOGIN_I18N_NAMESPACE,
} from '@supplierRegistration/services/supplierRegistrationGuestSessionService';

// antd 3's Modal predates React 18's stricter Component<P> typing and doesn't declare `children`
// on ModalProps; this is the narrowest fix for a component this codebase otherwise only ever
// uses imperatively (Modal.info/Modal.confirm, which take `content` instead).
const Modal = AntModal as unknown as FC<PropsWithChildren<ModalProps>>;

export interface EmailVerificationModalProps {
  open: boolean;
  onVerified: (email: string) => void;
  onClose: () => void;
}

/**
 * Wraps the guestLogin module's decoupled email/code screens in a modal, so a supplier can
 * prove ownership of an email address before the document-upload endpoints (Task 12) trust it.
 * The screens read their store via context, so this provides a standalone infra-stores-shaped
 * object -- keyed to `guestSession` -- rather than requiring the whole supplierRegistration mode
 * to become a GuestInfraStores host the way ar mode is.
 */
const EmailVerificationModal: FC<EmailVerificationModalProps> = observer((props) => {
  const { open, onVerified, onClose } = props;
  const theme = useTheme<SupplierRegistrationMode>();

  const { isVerified, verifiedEmail, isUserStartedLogin } = guestSession;

  // Restores an already-verified session from storage as soon as this component exists,
  // regardless of `open` -- so a caller can check guestSession.isVerified before ever opening it.
  useMountEffect(() => {
    void guestSession.init();
  });

  // Tracks whether isUserStartedLogin has actually turned true for the current `open` cycle, so
  // the render where it is still catching up to our own setIsUserStartedLogin(true) below isn't
  // misread as the enter-email screen's Back button (which is the only other thing that flips it
  // back to false, once it has been true).
  const hasStartedRef = React.useRef(false);

  React.useEffect(() => {
    if (open) {
      hasStartedRef.current = false;
      guestSession.setIsUserStartedLogin(true);
    }
  }, [open]);

  React.useEffect(() => {
    if (open && isVerified && verifiedEmail) {
      onVerified(verifiedEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isVerified, verifiedEmail]);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    if (isUserStartedLogin) {
      hasStartedRef.current = true;
      return;
    }

    // The enter-email screen's Back button calls setIsUserStartedLogin(false) -- there is
    // nowhere further back to go inside this modal, so treat it as a request to close.
    if (hasStartedRef.current) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isUserStartedLogin]);

  // GuestLoginBrandedTheme expects brand.clientName; supplierRegistration's theme carries
  // brand.customerName instead -- map it explicitly or the guestLogin screens misread the brand.
  const guestTheme: GuestLoginBrandedTheme = {
    ...theme,
    brand: theme.brand ? { ...theme.brand, clientName: theme.brand.customerName } : undefined,
  };

  const syntheticInfraStores = {
    guestLoginStore: guestSession,
    themeStore: { theme: guestTheme },
  };

  return (
    <Modal visible={open} onCancel={onClose} footer={null} destroyOnClose centered width={520}>
      <MobXProviderContext.Provider value={{ infraStores: syntheticInfraStores }}>
        <GuestLoginI18nNamespaceProvider value={SUPPLIER_REGISTRATION_GUEST_LOGIN_I18N_NAMESPACE}>
          <ModalContent>
            {/* A full-page SSO redirect would discard the in-progress registration form behind this modal. */}
            <GuestLoginEmailFlow showSso={false} />
          </ModalContent>
        </GuestLoginI18nNamespaceProvider>
      </MobXProviderContext.Provider>
    </Modal>
  );
});

export default EmailVerificationModal;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
`;
